import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { useMarketRateSdApiV1ShipmentsShipmentIdMarketRateSdGet } from '#/shared/api/generated/shipment/theCargoShipment'
import type {
  SDMarketRateResponse,
  SDMarketRateResponseRaw,
  SDMarketSummary,
  SDPricePoint,
} from '#/shared/api/generated/shipment/models'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'

import type { MarketRateStats } from './types'

interface UseSuggestedPriceSdArgs {
  shipmentId: string | undefined
  enabled?: boolean
}

interface UseSuggestedPriceSdResult {
  stats: MarketRateStats | undefined
  /** True whenever a request is in flight, including a manual refetch over stale data. */
  isFetching: boolean
  errorMessage: string | undefined
  refetch: () => void
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

interface RawPrice {
  price: number | undefined
  perMi: number | undefined
  confidence: number | undefined
}

/** Verbatim Super Dispatch payload — `raw.data.{price,price_per_mile,confidence}`. */
function readRaw(raw: SDMarketRateResponseRaw | undefined): RawPrice {
  const data = asRecord(asRecord(raw)?.['data'])
  return {
    price: asNumber(data?.['price']),
    perMi: asNumber(data?.['price_per_mile']),
    confidence: asNumber(data?.['confidence']),
  }
}

interface Band {
  recommended: number | undefined
  recommendedPerMi: number | undefined
  low: number | undefined
  lowPerMi: number | undefined
  high: number | undefined
  highPerMi: number | undefined
  miles: number | undefined
  confidence: number | undefined
}

function point(p: SDPricePoint | null | undefined): {
  price: number | undefined
  perMi: number | undefined
} {
  return { price: p?.price, perMi: p?.per_mile ?? undefined }
}

function fromSummary(summary: SDMarketSummary | null | undefined): Band {
  const rec = point(summary?.recommended)
  const low = point(summary?.low)
  const high = point(summary?.high)
  return {
    recommended: rec.price,
    recommendedPerMi: rec.perMi,
    low: low.price,
    lowPerMi: low.perMi,
    high: high.price,
    highPerMi: high.perMi,
    miles: summary?.miles ?? undefined,
    confidence: summary?.confidence ?? undefined,
  }
}

/**
 * Merge SD's normalised `summary` band with the verbatim `raw` payload, the
 * latter as a fallback for any tier SD left `null`. Returns `undefined` when no
 * price could be sourced from either — the badge stays hidden in that case.
 */
function toStats(response: SDMarketRateResponse): MarketRateStats | undefined {
  const band = fromSummary(response.summary)
  const raw = readRaw(response.raw)
  const recommended = band.recommended ?? raw.price
  if (recommended === undefined) return undefined
  return {
    low: band.low ?? recommended,
    lowPerMi: band.lowPerMi,
    recommended,
    recommendedPerMi: band.recommendedPerMi ?? raw.perMi,
    high: band.high ?? recommended,
    highPerMi: band.highPerMi,
    referenceDistanceMi: band.miles,
    confidence: band.confidence ?? raw.confidence,
  }
}

function buildResult(
  data: SDMarketRateResponse | undefined,
  isFetching: boolean,
  error: unknown,
  t: TFunction,
): Omit<UseSuggestedPriceSdResult, 'refetch'> {
  if (error) {
    return {
      stats: undefined,
      isFetching,
      errorMessage:
        getApiErrorDetail(error) ?? t('table.marketRate.fetchError'),
    }
  }
  // No usable price → no stats and no error: the badge renders nothing.
  return {
    stats: data ? toStats(data) : undefined,
    isFetching,
    errorMessage: undefined,
  }
}

/**
 * Super Dispatch recommended-price lookup for the "Check Rate" badge
 * (`GET /shipments/{id}/market-rate-sd`). Sources the recommended price, its
 * per-mile rate, and provider confidence from SD's `summary`, falling back to
 * the verbatim `raw` payload. The badge hides itself when SD returns no price.
 */
export function useSuggestedPriceSd({
  shipmentId,
  enabled = true,
}: UseSuggestedPriceSdArgs): UseSuggestedPriceSdResult {
  const { t } = useTranslation()
  const queryEnabled = Boolean(enabled && shipmentId)
  const { data, isFetching, error, refetch } =
    useMarketRateSdApiV1ShipmentsShipmentIdMarketRateSdGet(
      shipmentId ?? '',
      undefined,
      { query: { enabled: queryEnabled } },
    )

  const triggerRefetch = () => {
    void refetch()
  }

  if (!queryEnabled) {
    return {
      stats: undefined,
      isFetching: false,
      errorMessage: undefined,
      refetch: triggerRefetch,
    }
  }

  return {
    ...buildResult(data?.data, isFetching, error, t),
    refetch: triggerRefetch,
  }
}
